import React, {FC, RefObject} from "react";
import {Button, ButtonGroup, HStack, Textarea, TextareaProps} from "@chakra-ui/react";
import {useTextHistory} from "../../hooks/useTextHistory";
import {
    DEALERSHIP_NAME_TAG,
    insertTextAtSelection,
    wrapSelectionWithMarkers,
} from "../../shared/utils/inactiveOrderReminderMailPlainText";

type Props = Omit<TextareaProps, "onChange" | "value"> & {
    value: string;
    onChange: (value: string) => void;
    inputRef?: RefObject<HTMLTextAreaElement>;
    showDealershipTag?: boolean;
    dealershipTagButtonLabel?: string;
};

export const RichPlainTextarea: FC<Props> = ({
    value,
    onChange,
    inputRef,
    showDealershipTag = true,
    dealershipTagButtonLabel = "+ dealership name",
    ...textareaProps
}) => {
    const {setValue, handleKeyDown} = useTextHistory(value, onChange);

    const applyFormat = (before: string, after: string) => {
        const el = inputRef?.current;
        const start = el?.selectionStart ?? value.length;
        const end = el?.selectionEnd ?? value.length;
        const result = wrapSelectionWithMarkers(value, start, end, before, after);
        if (!result) {
            return;
        }
        const {nextValue, cursor} = result;
        setValue(nextValue);
        if (el) {
            window.requestAnimationFrame(() => {
                el.focus();
                el.setSelectionRange(cursor, cursor);
            });
        }
    };

    const insertTag = () => {
        const el = inputRef?.current;
        const start = el?.selectionStart ?? value.length;
        const end = el?.selectionEnd ?? value.length;
        const {nextValue, cursor} = insertTextAtSelection(value, DEALERSHIP_NAME_TAG, start, end);
        setValue(nextValue);
        if (el) {
            window.requestAnimationFrame(() => {
                el.focus();
                el.setSelectionRange(cursor, cursor);
            });
        }
    };

    return (
        <>
            <HStack spacing={1} mb={1} flexWrap="wrap">
                <ButtonGroup size="xs" isAttached variant="outline">
                    <Button
                        aria-label="Bold"
                        fontWeight="bold"
                        minW="28px"
                        px={2}
                        onClick={() => applyFormat("**", "**")}
                    >
                        B
                    </Button>
                    <Button
                        aria-label="Italic"
                        fontStyle="italic"
                        minW="28px"
                        px={2}
                        onClick={() => applyFormat("*", "*")}
                    >
                        I
                    </Button>
                </ButtonGroup>
                {showDealershipTag && (
                    <Button size="xs" variant="outline" onClick={insertTag}>
                        {dealershipTagButtonLabel}
                    </Button>
                )}
            </HStack>
            <Textarea
                ref={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                {...textareaProps}
            />
        </>
    );
};

export default RichPlainTextarea;
